import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { DocumentStatusType } from '../models';
import { CreateTestDocumentResponse, ResendResult } from '../models/resend-document-status';
import { TrackingSearchResult } from '../models/tracking-search-result';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { DownloaderService } from '../../shared/download/downloader.service';
import { ResendDocument } from './tracking-shared-action-methods.service';
import { StatusEventSearchItem, StatusEventSearchLogItem } from '../models/status-event-search-item';
import { StatusEventSearchObject } from '../models/status-event-search-object';
import { InitConfiguration } from '../../distribution/models/init-configuration';
import { TrackingSearchDetailsResult } from '../models/tracking-search-details-result';
import { MessageRelationType } from '../models/message-relation-types';
import { ExceptionInstance } from 'src/app/error-handling/models/exception-instance';
import { TrackingUiAction } from 'src/app/developer/models/tracking-ui-action';
import { TrackingActionDocument } from '../models/tracking-action-document';
import { TrackingActionValidationResultSingle } from '../models/tracking-action-validate-result-single';
import { TrackingActionValidateResult } from '../models/tracking-action-validate-result';
import { DataFieldGroup, DataFieldValue } from 'src/app/shared/datafields/models';
import { firstValueFrom } from 'rxjs';
import { TrackingResendDocumentResult } from '../tracking-resend-modal.component';
import { ExceptionInstanceInterpretation } from 'src/app/error-handling/models/exception-instance-interpretation';
import { InterpretationVoteRequest } from '../models/interpretation-vote-request';

export interface CdmSampleResult {
  DocumentTypeId: number;
  TimeStamp: Date;
  FileName: string;
  FileSize: number;
  Xml: string;
};

export interface TrackingIdContainer {
  DocumentIds: number[];
  InterchangeInIds: number[];
}

export interface ChangeStakeholderContainer extends TrackingIdContainer {
  StakeholderId: number;
  Comment: string;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService {

  public static readonly maxNumberOfDocumentsToShow = 20;

  constructor(private httpService: JsonHttpService, private downloaderServicer: DownloaderService) { }

  // METHODS FOR THE SEARCH CONTROLLER - START

  public searchStatusEvents(statusEventSearchObject: StatusEventSearchObject): Promise<StatusEventSearchItem[]> {
    const options = { params: new HttpParams() };
    for (const propertyName in statusEventSearchObject) {
      if (statusEventSearchObject[propertyName] != undefined && statusEventSearchObject[propertyName] != null) {
        options.params = options.params.set(propertyName, statusEventSearchObject[propertyName]);
      }
    }
    return this.httpService.get('/api/tracking/search/search-status-events', options).toPromise();
  }

  public getStatusEventLogs(runId: number): Promise<StatusEventSearchLogItem[]> {
    const options = { params: new HttpParams().set('runId', `${runId}`) };
    return this.httpService.get('/api/tracking/search/get-status-event-logs', options).toPromise();
  }

  public searchDocuments(documentSearchObject: any): Promise<TrackingSearchResult> {
    const options = { params: new HttpParams() };
    for (const propertyName in documentSearchObject) {
      options.params = options.params.set(propertyName, documentSearchObject[propertyName]);
    }

    return this.httpService.get('/api/tracking/search/search-documents', options).toPromise();
  }

  public getCdmSampleForDocumentType(documentTypeId: number): Promise<CdmSampleResult> {
    const options = { params: new HttpParams().set('documentTypeId', `${documentTypeId}`) };
    return firstValueFrom(this.httpService.getTyped<CdmSampleResult>('/api/tracking/search/get-cdm-sample-for-document-type', options).pipe(
      // Ensure that TimeStamp is Date
      map(result => result ? { ...result, TimeStamp: new Date(result.TimeStamp) } : null)
    ));
  }

  public getDocuments(documentIds: number[], interchangeInIds: number[], currentDocumentCount: number = 0): Promise<TrackingSearchDetailsResult> {
    const options = { params: new HttpParams() };
    if (currentDocumentCount > 0) {
      options.params = options.params.set('currentDocumentCount', currentDocumentCount);
    }
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/search/get-documents', trackingIdContainer, options).pipe(
      // Ensure that all CreatedDate in all MessageRelations in all Documents are Date
      map((result: TrackingSearchDetailsResult) => ({
        ...result, Documents: result.Documents?.map(document => ({
          ...document, MessageRelations: document.MessageRelations?.map(relation => ({
            ...relation, CreatedDate: new Date(relation.CreatedDate)
          }))
        }))
      }))
    ).toPromise();
  }

  public GetResendDocumentResultByInterchangeIds(interchangeInIds: number[]): Promise<TrackingResendDocumentResult[]> {
    const trackingIdContainer = this.generateTrackingIdContainer([], interchangeInIds);
    return this.httpService.post('/api/tracking/search/get-resend-documentresult-by-interchangeids', trackingIdContainer).toPromise();
  }

  public checkDocumentDownloadAccess(documentIds: number[], interchangeInIds: number[]): Promise<boolean> {
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/search/data-access-check-document-download', trackingIdContainer).toPromise();
  }

  public getStatusTypesForDocument(documentId: number): Promise<DocumentStatusType[]> {
    const options = { params: new HttpParams().set('documentId', `${documentId}`) };
    return this.httpService.get('/api/tracking/search/get-status-types-for-document', options).toPromise();
  }

  public getStatusTypesForInterchangeIn(interchangeInId: number): Promise<DocumentStatusType[]> {
    const options = { params: new HttpParams().set('interchangeInId', `${interchangeInId}`) };
    return this.httpService.get('/api/tracking/search/get-status-types-for-interchangein', options).toPromise();
  }

  public getExceptionInstancesForDocument(documentId: number): Promise<ExceptionInstance[]> {
    const options = { params: new HttpParams().set('documentId', `${documentId}`) };
    return this.httpService.get('/api/tracking/search/get-exception-instances-for-document', options).toPromise();
  }

  public getExceptionInstancesForInterchangeIn(interchangeInId: number): Promise<ExceptionInstance[]> {
    const options = { params: new HttpParams().set('interchangeInId', `${interchangeInId}`) };
    return this.httpService.get('/api/tracking/search/get-exception-instances-for-interchangein', options).toPromise();
  }

  public downloadZipFile(documentIds: number[], interchangeInIds: number[], fileTypes: string[]): Promise<void> {
    const options = { params: new HttpParams() };
    for (const fileType of fileTypes) {
      options.params = options.params.append('fileType', fileType);
    }

    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('api/tracking/search/get-zip-download-token', trackingIdContainer, options).toPromise().then((token) => {
      if (token) {
        return this.downloaderServicer.downloadFile(token);
      }
      else {
        return Promise.reject('No valid token returned!');
      }
    });
  }

  public downloadCdmAttachment(documentId: number): Promise<void> {
    const options = { params: new HttpParams().set('documentId', `${documentId}`) };
    return this.httpService.get('api/tracking/search/get-cdm-attachment-download-token', options).toPromise().then((token) => {
      if (token) {
        return this.downloaderServicer.downloadFile(token);
      }
      else {
        return Promise.reject('No valid token returned!');
      }
    });
  }

  // METHODS FOR THE SEARCH CONTROLLER - END


  // METHODS FOR THE TRACKING-TEST-FILE CONTROLLER - START

  public uploadDocuments(fileGuids: string[], initConfig: InitConfiguration): Promise<CreateTestDocumentResponse> {
    const uploadInfo = {
      FileGuids: fileGuids,
      InitConfiguration: initConfig
    };

    return this.httpService.post('/api/tracking/test-file/upload', uploadInfo).toPromise();
  }

  // METHODS FOR THE TRACKING-TEST-FILE CONTROLLER - END


  // METHODS FOR THE TRACKING CONTROLLER - START

  public resendDocuments(resendDocuments: ResendDocument[], comment: string, resendByInterchange: boolean, resendGenerateNewTransmissionId: boolean): Promise<ResendResult> {
    const resendInfo = {
      ResendDocuments: resendDocuments,
      Comment: comment,
      ResendByInterchange: resendByInterchange,
      ResendGenerateNewTransmissionId: resendGenerateNewTransmissionId
    };

    return this.httpService.post('/api/tracking/resend-documents', resendInfo).toPromise();
  }

  public reprocessStatusEventRuns(runIds: number[], comment: string): Promise<void> {
    const options = { params: new HttpParams().set('comment', comment || '') };
    const idContainer = {
      Ids: runIds
    }
    return this.httpService.post('api/tracking/reprocess-status-event-runs', idContainer, options).toPromise();
  }

  public manuallyHandleEventRuns(runIds: number[], comment: string): Promise<any> {
    const options = { params: new HttpParams().set('comment', comment || '') };
    const idContainer = {
      Ids: runIds
    }
    return this.httpService.post('/api/tracking/manually-handle-event-runs', idContainer, options).toPromise();
  }

  public reCreateDocuments(documentIds: number[], interchangeInIds: number[], comment: string): Promise<void> {
    const options = { params: new HttpParams().set('comment', comment) };
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/recreate-documents', trackingIdContainer, options).toPromise();
  }

  public reCreateCheckDocuments(documentIds: number[], interchangeInIds: number[]): Promise<any> {
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/recreate-check-documents', trackingIdContainer).toPromise();
  }

  public changeStakeholder(documentIds: number[], interchangeInIds: number[], stakeholderId: number, comment: string): Promise<any> {
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    const data: ChangeStakeholderContainer = { ...trackingIdContainer, StakeholderId: stakeholderId, Comment: comment };
    return firstValueFrom(this.httpService.postTyped<any>('/api/tracking/change-stakeholder', data, { observe: 'response' }));
  }

  public setPriority(documentIds: number[], interchangeInIds: number[], priorityValue: number): Promise<void> {
    const options = { params: new HttpParams().set('priorityValue', `${priorityValue}`) };
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/set-priority', trackingIdContainer, options).toPromise();
  }

  public deleteDocuments(documentIds: number[], interchangeInIds: number[]): Promise<any> {
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/delete-documents', trackingIdContainer).toPromise();
  }

  public manuallyHandleDocuments(documentIds: number[], interchangeInIds: number[], comment: string): Promise<any> {
    const options = { params: new HttpParams().set('comment', comment || '') };
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/manually-handle-documents', trackingIdContainer, options).toPromise();
  }

  public setDocumentsOnHold(documentIds: number[], interchangeInIds: number[], comment: string): Promise<any> {
    const options = { params: new HttpParams().set('comment', comment || '') };
    const trackingIdContainer = this.generateTrackingIdContainer(documentIds, interchangeInIds);
    return this.httpService.post('/api/tracking/set-documents-on-hold', trackingIdContainer, options).toPromise();
  }

  public getInterchangeGuidFromInterchangeId(interchangeId: number): Promise<string> {
    const options = { params: new HttpParams().set('interchangeId', `${interchangeId}`) };
    return this.httpService.get('/api/interchange/get-guid', options).toPromise();
  }

  public getMessageRelationTypes(): Promise<MessageRelationType[]> {
    return this.httpService.getTyped<MessageRelationType[]>('/api/tracking/messagerelations/get-message-relation-types').toPromise();
  }

  // METHODS FOR THE TRACKING CONTROLLER - END

  // METHODS FOR THE TRACKING UI ACTION CONTROLLER - BEGIN

  public getTrackingUiActionsForDetailsPage(): Promise<TrackingUiAction[]> {
    return this.httpService.get('/api/tracking/ui-actions/get-tracking-ui-actions-for-details-page').toPromise();
  }

  public getTrackingUiActionsForTrackingResultPage(): Promise<TrackingUiAction[]> {
    return this.httpService.get('/api/tracking/ui-actions/get-tracking-ui-actions-for-tracking-result-page').toPromise();
  }

  public validateTrackingActionSingle(trackingActionId: number, document: TrackingActionDocument): Promise<TrackingActionValidationResultSingle> {
    return this.httpService.post('/api/tracking/ui-actions/' + trackingActionId + '/validate-action-single', document).toPromise();
  }

  public validateTrackingAction(trackingActionId: number, documents: TrackingActionDocument[]): Promise<TrackingActionValidateResult> {
    return this.httpService.post('/api/tracking/ui-actions/' + trackingActionId + '/validate-action', documents).toPromise();
  }

  public getTrackingUiActionRuntimeDataFieldGroups(artifactId: string): Promise<DataFieldGroup[]> {
    return this.httpService.get('/api/tracking/ui-actions/get-tracking-ui-action-runtime-datafields/' + artifactId).toPromise();
  }

  public performTrackingAction(trackingUiActionEntity: TrackingUiAction, documents: TrackingActionDocument[],
    runTimeConfigurationValues: DataFieldValue[]): Promise<{ Success: boolean; StatusText: string }> {
    const actionArgs = {
      TrackingUiActionEntity: trackingUiActionEntity,
      RunTimeConfigurationValues: runTimeConfigurationValues,
      Documents: documents
    };
    return this.httpService.post('/api/tracking/ui-actions/perform-action', actionArgs).toPromise();
  }

  // METHODS FOR THE TRACKING UI ACTION CONTROLLER - END

  private generateTrackingIdContainer(documentIds: number[], interchangeInIds: number[]): TrackingIdContainer {
    return {
      DocumentIds: documentIds.filter(Number),
      InterchangeInIds: interchangeInIds.filter(Number)
    };
  }

  hasAIAccess(): Promise<boolean> {
    return this.httpService.get('/api/tracking-ai/has-access').toPromise();
  }

  getAIErrorInterpretation(exceptionInstanceId: number, reCreateAiInterpretation: boolean): Promise<ExceptionInstanceInterpretation> {
    const options = { 
      params: new HttpParams()
      .set('exceptionInstancesId', `${exceptionInstanceId}`)
      .set('reCreateAIInterpretation', `${reCreateAiInterpretation}`) 
    };
    
    return this.httpService.get('/api/tracking-ai/get-ai-interpretation', options).toPromise();
  }

  voteAIInterpretation(request: InterpretationVoteRequest): Promise<void> {
    return this.httpService.post('/api/tracking-ai/vote-ai-interpretation', request).toPromise();
  }
}
